import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { scrollToId } from "../lib/scroll";

const LINKS = [
  { id: "the-decision", label: "The Decision" },
  { id: "system", label: "Intelligence System" },
  { id: "deliverables", label: "Deliverables" },
  { id: "engagements", label: "Engagements" },
  { id: "why-enexur", label: "Why ENEXUR" },
];

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const goSection = (e, id) => {
    e.preventDefault();
    setOpen(false);
    if (pathname !== "/india-market-intelligence") navigate(`/india-market-intelligence#${id}`);
    else scrollToId(id);
  };

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled || open
            ? "border-b border-gold/15 bg-ink/85 backdrop-blur-md"
            : "border-b border-transparent bg-transparent"
        }`}
      >
        <div className="wrap flex h-[76px] items-center justify-between">
          <Link
            to="/india-market-intelligence"
            data-testid="nav-logo-link"
            aria-label="ENEXUR — product page"
          >
            <Logo />
          </Link>

          <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
            {LINKS.map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                onClick={(e) => goSection(e, l.id)}
                data-testid={`nav-link-${l.id}`}
                className="font-mono text-[11px] uppercase tracking-[0.22em] text-mist transition-colors duration-200 hover:text-gold"
              >
                {l.label}
              </a>
            ))}
            <Link
              to="/client-intelligence-brief"
              data-testid="nav-cta"
              className="border border-gold/50 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.22em] text-gold transition-all duration-300 hover:bg-gold hover:text-ink"
            >
              Start Client Brief
            </Link>
          </nav>

          <div className="flex items-center gap-4 lg:hidden">
            <Link
              to="/client-intelligence-brief"
              data-testid="nav-cta-mobile-inline"
              className="hidden border border-gold/50 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-gold transition-all duration-300 hover:bg-gold hover:text-ink sm:block"
            >
              Start Client Brief
            </Link>
            <button
              onClick={() => setOpen((o) => !o)}
              data-testid="nav-mobile-toggle"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              className="border border-gold/30 p-2.5 text-gold transition-colors hover:bg-gold/10"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 flex flex-col justify-center bg-ink/[0.98] px-8 lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <nav aria-label="Mobile" className="mx-auto w-full max-w-lg">
              {LINKS.map((l, i) => (
                <motion.a
                  key={l.id}
                  href={`#${l.id}`}
                  onClick={(e) => goSection(e, l.id)}
                  initial={{ opacity: 0, x: -18 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5 }}
                  className="block border-b border-gold/12 py-5 font-display text-2xl text-ivory transition-colors hover:text-gold"
                >
                  {l.label}
                </motion.a>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.42, duration: 0.5 }}
                className="pt-10"
              >
                <Link
                  to="/client-intelligence-brief"
                  data-testid="nav-cta-mobile"
                  className="btn-gold w-full"
                >
                  Start Client Brief
                </Link>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
